// ===== UNIFIED CLIENT ORDERING (2026-08-05) =====
// The desktop Clients page and the estimator app order clients with this
// ONE module (Jaylen + brothers: the two surfaces had unrelated orders —
// desktop was data-entry order, estimator was walkthrough-lifecycle).
// Canonical sequence, soonest-action-first:
//
//   1. today       — walkthroughs happening TODAY in appointment-time
//                    order, with an in-progress walkthrough pinned to the
//                    very top (Julian: "open the app, see today's route").
//                    Walkthroughs COMPLETED today stay in this group until
//                    midnight, sunk to the bottom (Jaylen 2026-08-06: the
//                    section should match the board all day — completed
//                    ones vanishing read as missing). They graduate to
//                    their move-date bucket tomorrow.
//   2. upcoming_wt — future walkthroughs, date then appointment time.
//   3. moves       — clients whose MOVE date is today-or-later (and no
//                    pending walkthrough), soonest first. Completed-
//                    walkthrough clients with an upcoming move land here,
//                    not in the completed tail.
//   4. attention   — no walkthrough scheduled, no upcoming move: the
//                    "somebody should call these people" pool, newest
//                    lead first. (Past-due walkthroughs that never got
//                    marked land here too.)
//   5. completed   — walkthrough done, nothing else upcoming. Reference
//                    tail, most recently completed first.
//
// Inactive-status filtering stays the caller's job (the desktop chips).
// Change the rules HERE and both surfaces move together — same living-
// single-source pattern as leadPipeline.js and constants.js statuses.

const localTodayIso = () => new Date().toLocaleDateString('en-CA');

export const CLIENT_ORDER_LABELS = {
    today: "Today's Walkthroughs",
    upcoming_wt: 'Upcoming Walkthroughs',
    moves: 'Moves Coming Up',
    attention: 'Needs Attention',
    completed: 'Walkthrough Completed',
};

// ── Recently completed (Jaylen 2026-09-19 / 21) ──────────────────────────
// A cross-cut of the ordering above, for the "Recently completed" chip on
// BOTH the estimator app and the desktop Clients page: every walkthrough
// marked Complete in the last RECENT_WT_DAYS days, newest first, wherever
// the canonical buckets would file the client (a completed walkthrough
// with a move next week sits under "Moves Coming Up", which is exactly the
// one an estimator couldn't find again).
export const RECENT_WT_DAYS = 30;
export function recentlyCompletedWalkthroughs(clients, { days = RECENT_WT_DAYS, now = Date.now() } = {}) {
    const cutoff = new Date(now - days * 864e5).toISOString();
    return (clients || [])
        .filter(c => c.walkthrough_status === 'completed' && (c.walkthrough_completed_at || '') >= cutoff)
        .sort((a, b) => (b.walkthrough_completed_at || '').localeCompare(a.walkthrough_completed_at || ''));
}
// "Done Sep 18 4:14 PM · by Jaylen" for a completed client's row.
export function completedStamp(c, { todayIso } = {}) {
    if (!c?.walkthrough_completed_at) return '';
    const d = new Date(c.walkthrough_completed_at);
    if (Number.isNaN(d.getTime())) return '';
    const today = todayIso || localTodayIso();
    const ymd = d.toLocaleDateString('en-CA');
    const day = ymd === today ? 'Today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return [`Done ${day} ${time}`, c.walkthrough_completed_by ? `by ${c.walkthrough_completed_by}` : ''].filter(Boolean).join(' · ');
}

// clients: client rows · jobs: move_jobs (for WALKTHROUGH appointment
// times — the time lives on the job, not the client).
// Returns non-empty groups in canonical order: [{ key, label, items }].
export function groupClientsForOrder(clients, jobs, { todayIso } = {}) {
    const today = todayIso || localTodayIso();

    const wtTime = new Map();
    for (const j of (jobs || [])) {
        if (j.type === 'WALKTHROUGH' && j.client_id) wtTime.set(j.client_id, j.time || '');
    }

    const buckets = { today: [], upcoming_wt: [], moves: [], attention: [], completed: [] };
    for (const c of (clients || [])) {
        const wtPending = c.walkthrough_status !== 'completed';
        // in_progress pins to TODAY only when the walkthrough is actually
        // dated today-or-later — a stale in_progress from a past date
        // (e.g. never flipped to completed) used to squat in "Today's
        // Walkthroughs" forever (Jenna/William, 2026-08-07). Stale ones
        // fall through to the normal buckets below.
        if (c.walkthrough_status === 'in_progress' && (!c.walkthrough_date || c.walkthrough_date >= today)) buckets.today.push(c);
        // Completed-today intentionally included (no wtPending check).
        else if (c.walkthrough_date === today) buckets.today.push(c);
        else if (c.walkthrough_date && c.walkthrough_date > today && wtPending) buckets.upcoming_wt.push(c);
        else if (c.move_date && c.move_date >= today) buckets.moves.push(c);
        else if (c.walkthrough_status === 'completed') buckets.completed.push(c);
        else buckets.attention.push(c);
    }

    // Date + appointment time; time-less rows sink to the end of their day.
    const wtKey = (c) => `${c.walkthrough_date || ''}T${wtTime.get(c.id) || '23:59'}`;

    // In-progress pins top, pending in appointment order, completed-today
    // sinks to the bottom (still in appointment order).
    const todayRank = (c) => c.walkthrough_status === 'in_progress' ? 0 : c.walkthrough_status === 'completed' ? 2 : 1;
    buckets.today.sort((a, b) => {
        const ap = todayRank(a);
        const bp = todayRank(b);
        if (ap !== bp) return ap - bp;
        if (ap === 0) return (b.walkthrough_started_at || '').localeCompare(a.walkthrough_started_at || '');
        return wtKey(a).localeCompare(wtKey(b));
    });
    buckets.upcoming_wt.sort((a, b) => wtKey(a).localeCompare(wtKey(b)));
    buckets.moves.sort((a, b) => (a.move_date || '9999-99-99').localeCompare(b.move_date || '9999-99-99'));
    buckets.attention.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    buckets.completed.sort((a, b) => (b.walkthrough_completed_at || '').localeCompare(a.walkthrough_completed_at || ''));

    return ['today', 'upcoming_wt', 'moves', 'attention', 'completed']
        .map(key => ({ key, label: CLIENT_ORDER_LABELS[key], items: buckets[key] }))
        .filter(g => g.items.length > 0);
}
